import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLanguage } from "@/context/LanguageContext";
import { blogPosts, findPost } from "@/data/blogPosts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SITE = "https://4keeperz.ch";
const resolveImg = (src) => (src && src.startsWith("/") ? `${BACKEND}${src}` : src);

// Safely embed JSON inside a <script> tag: prevent early tag termination
// by escaping '</' sequences (e.g. '</script>') per OWASP recommendation.
const safeJsonLd = (obj) => JSON.stringify(obj).replace(/</g, "\\u003c");

const BlogIndex = () => {
  const { lang } = useLanguage();
  const tDe = lang === "de";

  useEffect(() => {
    document.title = tDe
      ? "Blog · Torwart-Wissen · 4keeperz"
      : "Blog · Goalkeeper Knowledge · 4keeperz";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        tDe
          ? "Guides, Pflegetipps und ehrliches Material-Wissen für Torhüter — von Keeperz für Keeperz."
          : "Guides, care tips and honest material knowledge for goalkeepers — by keepers, for keepers."
      );
    }
  }, [tDe]);

  // JSON-LD Blog schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "4keeperz Blog",
    url: `${SITE}/blog`,
    publisher: {
      "@type": "Organization",
      name: "4keeperz",
      url: SITE,
    },
    blogPost: blogPosts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title[lang] || p.title.de,
      datePublished: p.publishedAt,
      url: `${SITE}/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-24" data-testid="blog-index">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <Link
          to="/"
          data-testid="blog-back-home"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#00A651] font-condensed uppercase tracking-widest text-xs mb-10"
        >
          <ArrowLeft className="w-3 h-3" />
          {tDe ? "Zurück zur Startseite" : "Back to home"}
        </Link>

        <header className="mb-14">
          <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-3">
            {tDe ? "Knowledge Base" : "Knowledge Base"}
          </div>
          <h1 className="font-brush text-5xl md:text-6xl lg:text-7xl text-white leading-[0.9] mb-4">
            {tDe ? "Blog" : "Blog"}
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            {tDe
              ? "Guides, Pflegetipps und ehrliches Material-Wissen — by keeperz for keeperz. Kein Marketing-Bullshit, nur Substanz."
              : "Guides, care tips and honest material knowledge — by keepers, for keepers. No marketing fluff, just substance."}
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-list">
          {blogPosts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group border border-white/10 bg-[#0A0A0A] hover:border-[#00A651]/50 transition-colors flex flex-col overflow-hidden"
            >
              {p.cover && (
                <div className="aspect-[16/10] overflow-hidden bg-black">
                  <img
                    src={resolveImg(p.cover)}
                    alt={p.title[lang] || p.title.de}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-[10px] font-condensed uppercase tracking-widest text-white/50 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[#00A651]">
                    <Tag className="w-3 h-3" />
                    {p.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    {p.readMin} min
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {new Date(p.publishedAt).toLocaleDateString(tDe ? "de-CH" : "en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <h2 className="font-brush text-2xl md:text-3xl text-white leading-tight mb-3">
                  {p.title[lang] || p.title.de}
                </h2>
                <p className="text-white/60 text-sm leading-relaxed mb-5 flex-1">
                  {p.excerpt[lang] || p.excerpt.de}
                </p>
                <div className="inline-flex items-center gap-2 text-[#00A651] font-condensed uppercase tracking-widest text-xs group-hover:gap-3 transition-all">
                  {tDe ? "Weiterlesen" : "Read more"}
                  <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export const BlogPost = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const tDe = lang === "de";
  const post = findPost(slug);

  useEffect(() => {
    if (!post) return;
    const title = `${post.title[lang] || post.title.de} · 4keeperz`;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", post.excerpt[lang] || post.excerpt.de);
    window.scrollTo(0, 0);
  }, [post, lang]);

  if (!post) return <Navigate to="/blog" replace />;

  // JSON-LD BlogPosting schema for SEO
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title[lang] || post.title.de,
    description: post.excerpt[lang] || post.excerpt.de,
    image: post.cover ? resolveImg(post.cover) : undefined,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: { "@type": "Organization", name: "4keeperz" },
    publisher: {
      "@type": "Organization",
      name: "4keeperz",
      logo: {
        "@type": "ImageObject",
        url: `${SITE}/og-image.png`,
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${post.slug}` },
    keywords: post.keywords?.join(", "),
  };

  const content = post.content[lang] || post.content.de;

  return (
    <article className="min-h-screen bg-black text-white pt-32 pb-24" data-testid="blog-post">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />
      <div className="mx-auto max-w-[820px] px-6 md:px-12">
        <Link
          to="/blog"
          data-testid="blog-back-list"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#00A651] font-condensed uppercase tracking-widest text-xs mb-8"
        >
          <ArrowLeft className="w-3 h-3" />
          {tDe ? "Alle Artikel" : "All articles"}
        </Link>

        <header className="mb-10">
          <div className="flex items-center gap-3 text-[10px] font-condensed uppercase tracking-widest text-white/50 mb-4">
            <span className="inline-flex items-center gap-1.5 text-[#00A651]">
              <Tag className="w-3 h-3" />
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {post.readMin} min {tDe ? "Lesezeit" : "read"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              {new Date(post.publishedAt).toLocaleDateString(tDe ? "de-CH" : "en-GB", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>
          <h1
            className="font-brush text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] mb-5"
            data-testid="blog-post-title"
          >
            {post.title[lang] || post.title.de}
          </h1>
          <p className="text-white/70 text-lg leading-relaxed">
            {post.excerpt[lang] || post.excerpt.de}
          </p>
        </header>

        {post.cover && (
          <div className="aspect-[16/9] mb-12 overflow-hidden bg-[#0A0A0A] border border-white/10">
            <img
              src={resolveImg(post.cover)}
              alt={post.title[lang] || post.title.de}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div
          className="blog-prose text-white/85 leading-relaxed"
          data-testid="blog-post-body"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>

        {/* CTA */}
        <div className="mt-16 border border-[#00A651]/30 bg-[#00A651]/5 p-8" data-testid="blog-cta">
          <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-2">
            {tDe ? "Jetzt verfügbar" : "Now available"}
          </div>
          <h3 className="font-brush text-3xl md:text-4xl text-white leading-tight mb-3">
            {tDe ? "Bereit für deinen nächsten Handschuh?" : "Ready for your next glove?"}
          </h3>
          <p className="text-white/70 mb-5 leading-relaxed">
            {tDe
              ? "Mamba und Panther Edition – jetzt versandbereit. Schweizweit, ab CHF 80 Versand gratis."
              : "Mamba and Panther editions – now shipping. Switzerland-wide, free shipping over CHF 80."}
          </p>
          <Link
            to="/#products"
            data-testid="blog-cta-shop"
            className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008A43] text-black font-condensed uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors"
          >
            {tDe ? "Produkte ansehen" : "Browse products"}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Related posts */}
        <div className="mt-16">
          <div className="font-condensed uppercase tracking-[0.3em] text-xs text-white/50 mb-5">
            {tDe ? "Weitere Artikel" : "More posts"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {blogPosts
              .filter((p) => p.slug !== post.slug)
              .slice(0, 2)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-related-${p.slug}`}
                  className="group border border-white/10 bg-[#0A0A0A] hover:border-[#00A651]/50 p-5 transition-colors"
                >
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-[#00A651] mb-2">
                    {p.category}
                  </div>
                  <h4 className="font-brush text-xl text-white leading-tight">
                    {p.title[lang] || p.title.de}
                  </h4>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogIndex;
